import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { ArrowUp, ArrowDown, GripVertical } from "lucide-react";
import { DndContext, closestCenter, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, sortableKeyboardCoordinates, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

export const Field = ({ label, value, onChange, testid, area, max, type = "text", hint }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    {area
      ? <Textarea rows={3} className="mt-1" value={value} maxLength={max} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
      : <Input type={type} className="mt-1" value={value} maxLength={max} onChange={(e) => onChange(type === "number" ? Number(e.target.value) : e.target.value)} data-testid={testid} />}
    {hint && <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>}
  </div>
);

export const VisibleSwitch = ({ checked, onChange, testid, disabled }) => (
  <label className="flex items-center gap-2 text-xs">
    <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} disabled={disabled} />{checked ? "Yayında" : "Gizli"}
  </label>
);

const OrderRow = ({ k, i, last, label, hidden, onMove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: k });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-center justify-between gap-1 rounded-lg px-2 py-1.5 text-sm ${isDragging ? "bg-primary/20 shadow-lg relative z-10" : "bg-muted/40"}`} data-testid={`cms-order-${k}`}>
      <button type="button" className="touch-none cursor-grab active:cursor-grabbing p-1 text-muted-foreground" {...attributes} {...listeners} aria-label="Sürükle" data-testid={`cms-order-drag-${k}`}><GripVertical className="w-4 h-4" /></button>
      <span className={`flex-1 min-w-0 truncate ${hidden ? "text-muted-foreground line-through" : ""}`}>{i + 1}. {label}</span>
      <div className="flex gap-1 shrink-0">
        <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === 0} onClick={() => onMove(i, -1)} data-testid={`cms-order-up-${k}`}><ArrowUp className="w-4 h-4" /></Button>
        <Button size="icon" variant="ghost" className="h-7 w-7" disabled={last} onClick={() => onMove(i, 1)} data-testid={`cms-order-down-${k}`}><ArrowDown className="w-4 h-4" /></Button>
      </div>
    </div>
  );
};

export const OrderList = ({ order, onMove, onReorder, labelOf, isHidden }) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const end = ({ active, over }) => { if (over && active.id !== over.id) onReorder(arrayMove(order, order.indexOf(active.id), order.indexOf(over.id))); };
  return (
    <div className="rounded-2xl border p-3 space-y-2" data-testid="cms-order">
      <div className="font-semibold text-sm">Bölüm sırası</div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={end}>
        <SortableContext items={order} strategy={verticalListSortingStrategy}>
          {order.map((k, i) => <OrderRow key={k} k={k} i={i} last={i === order.length - 1} label={labelOf(k)} hidden={isHidden(k)} onMove={onMove} />)}
        </SortableContext>
      </DndContext>
      <p className="text-[11px] text-muted-foreground">Tutamaçtan sürükleyin veya okları kullanın. Üstü çizili bölümler gizlidir. Arama, konum ve acil yardım alanları karşılama bölümüyle birlikte taşınır.</p>
    </div>
  );
};

export const Card = ({ title, children, right, testid }) => (
  <div className="rounded-2xl border p-4 space-y-3" data-testid={testid}>
    <div className="flex items-center justify-between gap-2"><div className="font-semibold">{title}</div>{right}</div>
    {children}
  </div>
);
